export interface LinkPreview {
	url: string;
	domain: string | null;
	title: string | null;
	description: string | null;
	image: string | null;
	internal: boolean;
	route: string | null;
}

export type LinkPreviewState =
	| "full"
	| "no-image"
	| "loading"
	| "fallback"
	| "internal";

export interface InternalLinkActivation {
	route: string;
	payload: {
		path: string;
		objectId: string;
		communityId?: string;
		groupId?: string;
	};
}

type LinkPreviewSource = {
	linkPreviews?: unknown;
	link_previews?: unknown;
	description?: unknown;
};

type ActivationHandlers = {
	internal: (activation: InternalLinkActivation) => void;
	external: (url: string) => void;
};

const INTERNAL_PATHS = new Set([
	"posts",
	"videos",
	"documents",
	"resources",
	"offers",
	"marketplace",
	"events",
]);

const nullableText = (value: unknown): string | null => {
	if (typeof value !== "string") return null;
	const trimmed = value.trim();
	return trimmed || null;
};

export const getSafeExternalUrl = (value: unknown): string | null => {
	const raw = nullableText(value);
	if (!raw) return null;
	const candidate = /^(?:www\.|[^\s/]+\.[^\s/]+)/i.test(raw)
		? `https://${raw}`
		: raw;
	try {
		const parsed = new URL(candidate);
		return parsed.protocol === "http:" || parsed.protocol === "https:"
			? candidate
			: null;
	} catch (_error) {
		return null;
	}
};

const getDomain = (url: string): string | null => {
	const safeUrl = getSafeExternalUrl(url);
	if (!safeUrl) return null;
	try {
		return new URL(safeUrl).hostname.replace(/^www\./i, "") || null;
	} catch (_error) {
		return null;
	}
};

export const normalizeLinkPreviews = (
	source: LinkPreviewSource,
): LinkPreview[] => {
	const values = Array.isArray(source.linkPreviews)
		? source.linkPreviews
		: Array.isArray(source.link_previews)
		? source.link_previews
		: [];

	return values.flatMap((value) => {
		if (!value || typeof value !== "object") return [];
		const raw = value as Record<string, unknown>;
		const safeUrl = getSafeExternalUrl(raw.url);
		if (!safeUrl) return [];
		const preview: LinkPreview = {
			url: nullableText(raw.url) as string,
			domain: nullableText(raw.domain) || getDomain(safeUrl),
			title: nullableText(raw.title),
			description: nullableText(raw.description),
			image: getSafeExternalUrl(raw.image),
			internal: raw.internal === true,
			route: nullableText(raw.route),
		};
		return [preview];
	});
};

export const getInternalLinkActivation = (
	routeValue: unknown,
): InternalLinkActivation | null => {
	const rawRoute = nullableText(routeValue);
	if (!rawRoute || rawRoute.includes("://") || rawRoute.startsWith("//")) {
		return null;
	}
	try {
		const parsed = new URL(rawRoute.replace(/^\//, ""), "https://internal");
		const segments = parsed.pathname.split("/").filter(Boolean);
		if (
			segments.length < 2 ||
			!INTERNAL_PATHS.has(segments[0]) ||
			!/^\d+$/.test(segments[1])
		) {
			return null;
		}
		const communityId =
			parsed.searchParams.get("community_id") ||
			parsed.searchParams.get("communityId");
		const groupId =
			parsed.searchParams.get("group_id") ||
			parsed.searchParams.get("groupId");
		return {
			route: `/${rawRoute.replace(/^\//, "")}`,
			payload: {
				path: segments[0],
				objectId: segments[1],
				...(communityId ? { communityId } : {}),
				...(groupId ? { groupId } : {}),
			},
		};
	} catch (_error) {
		return null;
	}
};

export const getMembersiteInternalRoute = (
	activation: InternalLinkActivation,
	currentCommunityId: number | string | null | undefined,
): string | null => {
	const communityId = activation.payload.communityId || currentCommunityId;
	if (!communityId) return null;
	const search = new URLSearchParams({
		url: `${activation.payload.path}/${activation.payload.objectId}`,
		community_id: String(communityId),
	});
	return `/deeplink?${search.toString()}`;
};

export const getLinkPreviewState = (
	preview: LinkPreview,
	phase: "ready" | "loading" | "error" = "ready",
): LinkPreviewState => {
	if (phase === "loading") {
		return "loading";
	}
	if (
		phase === "error" ||
		(!preview.title && !preview.description && !preview.image)
	) {
		return "fallback";
	}
	if (preview.internal && getInternalLinkActivation(preview.route) !== null) {
		return "internal";
	}
	return preview.image ? "full" : "no-image";
};

export const getCarouselIndicator = (count: number, activeIndex: number) => {
	if (count <= 1) return { kind: "none" } as const;
	const safeIndex = Math.min(Math.max(activeIndex, 0), count - 1);
	if (count <= 8) {
		return { kind: "dots", count, activeIndex: safeIndex } as const;
	}
	return { kind: "counter", label: `${safeIndex + 1} / ${count}` } as const;
};

export const activateLinkPreview = (
	preview: LinkPreview,
	handlers: ActivationHandlers,
): boolean => {
	if (preview.internal) {
		const activation = getInternalLinkActivation(preview.route);
		if (activation) {
			handlers.internal(activation);
			return true;
		}
	}
	const externalUrl = getSafeExternalUrl(preview.url);
	if (!externalUrl) return false;
	handlers.external(externalUrl);
	return true;
};
